import { useState, type FormEvent } from 'react'
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth'
import { auth, googleProvider } from '../firebase'

export default function LoginPage({
  onAuthed,
  onBack,
  onRegister,
}: {
  onAuthed: () => void
  onBack?: () => void
  onRegister?: () => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetMsg, setResetMsg] = useState('')
  const [resetSending, setResetSending] = useState(false)

  const handleEmail = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
      onAuthed()
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string }
      if (e.code === 'auth/invalid-login-credentials' || e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password') {
        setError('Correo o contraseña incorrectos')
      } else if (e.code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Espera un momento')
      } else {
        setError('No se pudo iniciar sesión. Revisa tu conexión')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogle = async () => {
    setError('')
    setLoading(true)
    try {
      await signInWithPopup(auth, googleProvider)
      onAuthed()
    } catch (err: unknown) {
      const e = err as { code?: string }
      if (e.code !== 'auth/popup-closed-by-user') {
        setError('No se pudo iniciar con Google')
      }
    } finally {
      setLoading(false)
    }
  }

  const sendReset = async () => {
    setResetMsg('')
    setResetSending(true)
    try {
      await sendPasswordResetEmail(auth, resetEmail)
      setResetMsg('📧 Correo de recuperación enviado. Revisa tu bandeja.')
    } catch {
      setResetMsg('No se pudo enviar. Verifica que el correo esté bien escrito.')
    } finally {
      setResetSending(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-brand-600 via-brand-500 to-brand-700 p-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
        <div className="mb-6 text-center">
          {onBack && (
            <button
              onClick={onBack}
              className="mb-3 text-xs font-medium text-slate-400 hover:text-brand-600"
            >
              ← Volver al inicio
            </button>
          )}
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-600 text-3xl text-white">
            🛡️
          </div>
          <h1 className="text-2xl font-bold text-slate-800">KidSafe</h1>
          <p className="text-sm text-slate-500">Panel de control parental</p>
        </div>

        <form onSubmit={handleEmail} className="space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Correo electrónico"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
          />
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              title={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-slate-400 hover:text-brand-600"
            >
              {showPw ? '🙈' : '👁️'}
            </button>
          </div>
          <div className="text-right">
            <button
              type="button"
              onClick={() => setShowReset(!showReset)}
              className="text-xs font-medium text-brand-600 hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          {showReset && (
            <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
              <p className="mb-2 text-sm font-medium text-brand-800">
                Recuperar contraseña
              </p>
              <input
                type="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Tu correo electrónico"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-500"
              />
              <button
                type="button"
                onClick={sendReset}
                disabled={resetSending}
                className="mt-2 w-full rounded-xl bg-brand-600 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {resetSending ? 'Enviando…' : 'Enviar correo de recuperación'}
              </button>
              {resetMsg && <p className="mt-2 text-sm text-brand-700">{resetMsg}</p>}
            </div>
          )}
          {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-brand-600 py-3 font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
          >
            {loading ? 'Espera…' : 'Iniciar sesión'}
          </button>
        </form>

        <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />
          o
          <span className="h-px flex-1 bg-slate-200" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleGoogle}
            disabled={loading}
            className="rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
          >
            Google
          </button>
          <button
            onClick={onRegister}
            disabled={loading || !onRegister}
            className="rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
          >
            Crear cuenta
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          El registro completo (padre/hijo) se hace desde la app Android.
        </p>
      </div>
    </div>
  )
}
